import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Field, FieldProps, Formik } from 'formik'
import { Modal, Typography } from '@mui/material'
import tw from 'twin.macro'
import * as Yup from 'yup'
import { useDispatch } from 'react-redux'
import { CancelSmallMinor } from '@shopify/polaris-icons'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { BasicTextField } from 'src/components/shared/form/BasicTextField'
import { Button } from 'src/components/shared/Button'
import { colors } from 'src/theme'
import { Action, createAsyncAction } from 'src/utils/reduxUtils'
import { CreateApiKey } from 'src/constants/actionTypes'
import { handleApiError } from 'src/utils/errorHandlers'
import { CreateCustomerApiKeyParams, CreateCustomerApiKeyResponse } from 'src/interfaces/apiKey'
import { GSIcon } from 'src/components/shared/GSIcon'
import { WarningContainer } from 'src/components/shared/WarningContainer'
import { showToast } from 'src/utils/toast'

const nameSchema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
})

interface FormValues {
  name: string
}

interface Props {
  open: boolean
  onClose(): void
}

export const CreatePersonalAccessTokenModal: React.FC<Props> = ({ open, onClose }) => {
  const dispatch = useDispatch()

  const container = useMemo(() => document.getElementById('root'), [])

  const [phase, setPhase] = useState<'form' | 'token'>('form')
  const [created, setCreated] = useState<CreateCustomerApiKeyResponse | null>(null)

  useEffect(() => {
    if (!open) {
      setPhase('form')
      setCreated(null)
    }
  }, [open])

  const handleClose = useCallback(() => {
    setPhase('form')
    setCreated(null)
    onClose()
  }, [onClose])

  const handleCopy = useCallback(async () => {
    if (!created?.secret) {
      return
    }

    try {
      await navigator.clipboard.writeText(created.secret)
      showToast('Token copied to clipboard.')
    } catch {
      showToast('Unable to copy. Select the token and copy manually.', { variant: 'error' })
    }
  }, [created?.secret])

  const handleSubmitName = useCallback(
    async (values: FormValues, setSubmitting: (v: boolean) => void) => {
      setSubmitting(true)

      try {
        const payload: CreateCustomerApiKeyParams = { name: values.name.trim() }
        const result = await createAsyncAction<
          CreateCustomerApiKeyResponse,
          CreateCustomerApiKeyParams
        >(dispatch, CreateApiKey.request(payload) as Action<CreateCustomerApiKeyParams>)

        setCreated(result)
        setPhase('token')
      } catch (err) {
        handleApiError(err)
      } finally {
        setSubmitting(false)
      }
    },
    [dispatch],
  )

  return (
    <Modal
      container={container}
      open={open}
      onClose={handleClose}
      aria-labelledby="create-pat-modal-title"
      css={tw`flex justify-center items-center backdrop-blur z-[2001]`}
    >
      <div>
        {phase === 'form' && (
          <div
            css={tw`w-full max-w-[336px] overflow-hidden rounded-lg bg-white shadow-[0px_4px_48px_0px_rgba(0,0,0,0.25)]`}
          >
            <ModalHeader
              title="Create new token"
              titleId="create-pat-modal-title"
              onClose={handleClose}
            />
            <Formik<FormValues>
              initialValues={{ name: '' }}
              validationSchema={nameSchema}
              onSubmit={(values, { setSubmitting }) => handleSubmitName(values, setSubmitting)}
              enableReinitialize
            >
              {({ handleSubmit, isSubmitting }) => (
                <Fragment>
                  <div css={tw`flex flex-col gap-4 p-4`}>
                    <Field name="name">
                      {({ field, meta }: FieldProps<string>) => (
                        <BasicTextField
                          {...field}
                          name="name"
                          label="Token Name"
                          placeholder="Enter token name"
                          error={Boolean(meta.touched && meta.error)}
                          helperText={meta.touched ? meta.error : undefined}
                          css={tw`w-full`}
                          inputProps={{ 'data-testid': 'create-pat-name-input' }}
                        />
                      )}
                    </Field>
                  </div>
                  <div
                    css={tw`flex h-[60px] items-start border-0 border-t border-solid border-[#EDEDED] px-4 py-3`}
                  >
                    <div css={tw`flex w-full flex-1 items-start justify-between`}>
                      <Button
                        variant="contained"
                        size="small"
                        color="grey"
                        className={tw`px-8`}
                        onClick={handleClose}
                        data-testid="create-pat-cancel-btn"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        className={tw`px-8`}
                        loading={isSubmitting}
                        onClick={() => handleSubmit()}
                        data-testid="create-pat-submit-btn"
                      >
                        Create
                      </Button>
                    </div>
                  </div>
                </Fragment>
              )}
            </Formik>
          </div>
        )}

        {phase === 'token' && created && (
          <div
            css={tw`w-full max-w-[583px] overflow-hidden rounded-lg bg-white shadow-[0px_4px_48px_0px_rgba(0,0,0,0.25)]`}
          >
            <div css={tw`flex flex-col items-end bg-white px-4 pb-6 pt-4`}>
              <div
                data-testid="create-pat-token-close"
                css={tw`cursor-pointer rounded bg-[#EDEDED] p-1 hover:bg-[#DAD9E0] active:bg-[#B4AFC0]`}
                onClick={handleClose}
              >
                <CancelSmallMinor width={20} height={20} />
              </div>
              <div css={tw`flex w-full flex-col gap-8`}>
                <div css={tw`flex w-full flex-col items-center gap-2`}>
                  <GSIcon iconName="gs_unlocked" width={20} height={20} />
                  <div css={tw`flex w-full flex-col items-center gap-1 text-center`}>
                    <Typography id="create-pat-modal-title" variant="h4">
                      Personal Access Token Created
                    </Typography>
                    <Typography
                      variant="body2"
                      color={colors.SHADES_GREY_400}
                      css={tw`max-w-[360px]`}
                    >
                      Your token has been successfully generated.
                    </Typography>
                  </div>
                </div>

                <div
                  css={tw`flex w-full flex-row items-start gap-6 rounded-2xl border border-solid border-[#EDEDED] bg-white p-4`}
                >
                  <div css={tw`flex min-w-0 flex-1 flex-col gap-1`}>
                    <Typography
                      variant="h6"
                      color={colors.SHADES_GREY_400}
                      css={tw`min-w-0 max-w-full truncate`}
                      title={created.name}
                    >
                      {created.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={colors.BASICS_BLACK}
                      css={tw`mt-2 break-all`}
                      data-testid="create-pat-token-value"
                    >
                      {created.secret}
                    </Typography>
                  </div>
                  <Button
                    color="light"
                    size="small"
                    className={tw`flex-shrink-0 px-4 py-2`}
                    onClick={handleCopy}
                    rightIcon={
                      <span
                        css={[tw`inline-flex`, { '& path': { fill: colors.PRIMARY_PURPLE_500 } }]}
                      >
                        <GSIcon iconName="gs_square_behind_square_2" width={20} height={20} />
                      </span>
                    }
                  >
                    Copy
                  </Button>
                </div>

                <div css={tw`w-full min-w-0`}>
                  <WarningContainer
                    title="Important"
                    subtitle="Copy and store this token securely now. For security reasons, you won't be able to view it again. If you lose it, you'll need to generate a new token."
                  />
                </div>
              </div>
            </div>
            <div
              css={tw`flex h-[60px] items-center border-0 border-t border-solid border-[#EDEDED] px-4 py-3`}
            >
              <div css={tw`flex w-full flex-1 justify-end`}>
                <Button
                  variant="contained"
                  size="small"
                  color="grey"
                  className={tw`px-8`}
                  onClick={handleClose}
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
